import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../store';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, notificationSettingsTourSteps } from '../../shared/FeatureTour';
import { getNotificationPreferences, updateNotificationPreferences } from '../../../store/actions/Notifications';
import Toggle from '../../shared/TailwindComponents/Toggle';
import {
  PiCheckSquareOffsetLight,
  PiCalendarLight,
  PiUserListLight,
  PiBriefcaseLight,
  PiFileLight,
  PiDatabaseLight,
  PiFileTextLight,
  PiUsersLight,
  PiBuildingLight,
  PiUsersThreeLight,
  PiQuestionLight,
  PiGearLight,
  PiBellLight,
  PiCaretDownLight,
} from 'react-icons/pi';

interface NotificationSectionConfig {
  key: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  isAdminOnly?: boolean;
  isComingSoon?: boolean;
  notificationTypes: NotificationType[];
}

interface NotificationType {
  key: string;
  label: string;
  description: string;
  hasCustomValue?: boolean;
  customValueUnit?: 'minutes' | 'hours' | 'days';
  customValueMin?: number;
  customValueMax?: number;
}

const NOTIFICATION_SECTIONS: NotificationSectionConfig[] = [
  {
    key: 'tasks',
    title: 'Tasks',
    description: 'Get notified about task assignments, updates, and deadlines',
    icon: PiCheckSquareOffsetLight,
    notificationTypes: [
      { key: 'task_assigned_to_you', label: 'Task Assigned To You', description: 'When a task is assigned to you' },
      { key: 'task_added_collaborator', label: 'Added as Task Collaborator', description: 'When you\'re added as a collaborator' },
      { key: 'task_comment_on_task', label: 'Comment on Task', description: 'When someone comments on your task' },
      { key: 'task_attachment_uploaded', label: 'Attachment Uploaded', description: 'When a file is added to a task' },
      { key: 'task_status_changed', label: 'Task Status Changed', description: 'When a task status changes' },
      { key: 'task_due_date_approaching', label: 'Due Date Reminder', description: 'Get reminded before a task is due', hasCustomValue: true, customValueUnit: 'hours', customValueMin: 1, customValueMax: 72 },
    ],
  },
  {
    key: 'calendar',
    title: 'Calendar',
    description: 'Stay updated on calendar events and meeting invitations',
    icon: PiCalendarLight,
    isComingSoon: true,
    notificationTypes: [
      { key: 'calendar_event_created', label: 'Event Created', description: 'When you\'re invited to a calendar event' },
      { key: 'calendar_event_updated', label: 'Event Updated', description: 'When an event you\'re in is modified' },
      { key: 'calendar_event_reminder', label: 'Event Reminder', description: 'Get reminded before an event starts', hasCustomValue: true, customValueUnit: 'minutes', customValueMin: 5, customValueMax: 1440 },
    ],
  },
  {
    key: 'clients',
    title: 'Clients',
    description: 'Notifications for client portal activities and responses',
    icon: PiUserListLight,
    notificationTypes: [
      { key: 'client_accepted_invite', label: 'Client Accepted Invite', description: 'When a client accepts your portal invitation' },
      { key: 'client_comment_added', label: 'Client Comment', description: 'When a client adds a comment' },
      { key: 'client_document_uploaded', label: 'Client Document Upload', description: 'When a client uploads documents' },
      { key: 'client_status_update_requested', label: 'Status Update Requested', description: 'When a client requests a status update' },
      { key: 'lawyer_added_to_client', label: 'Lawyer Added to Client', description: 'When a lawyer is added to collaborate on a client' },
    ],
  },
  {
    key: 'matters',
    title: 'Matters',
    description: 'Notifications for matter management, drafts, and insights',
    icon: PiBriefcaseLight,
    notificationTypes: [
      { key: 'matter_assigned_to_you', label: 'Matter Assigned', description: 'When a matter is assigned to you' },
      { key: 'matter_added_collaborator', label: 'Added as Matter Collaborator', description: 'When you\'re added as a collaborator' },
      { key: 'matter_status_changed', label: 'Matter Status Changed', description: 'When a matter status changes' },
      { key: 'matter_document_uploaded', label: 'Document Uploaded', description: 'When documents are added to a matter' },
      { key: 'matter_collaborator_added', label: 'Access Granted', description: 'When someone is added to a matter' },
      { key: 'draft_updated', label: 'Draft Updated', description: 'When a draft document is updated' },
      { key: 'draft_marked_complete', label: 'Draft Marked Complete', description: 'When a draft is ready for review' },
      { key: 'insight_updated', label: 'Insight Updated', description: 'When an insight analysis is updated' },
      { key: 'insight_completed', label: 'Insight Completed', description: 'When an insight finishes processing' },
    ],
  },
  {
    key: 'files',
    title: 'Files',
    description: 'Notifications for file management and sharing',
    icon: PiFileLight,
    isComingSoon: true,
    notificationTypes: [
      { key: 'file_uploaded', label: 'File Uploaded', description: 'When a file is uploaded to a shared location' },
      { key: 'file_shared_with_you', label: 'File Shared', description: 'When a file is shared with you' },
      { key: 'file_version_updated', label: 'File Version Updated', description: 'When a file is updated' },
    ],
  },
  {
    key: 'datasources',
    title: 'Data Sources',
    description: 'Notifications for data source integrations, syncing, and sharing',
    icon: PiDatabaseLight,
    isComingSoon: true,
    notificationTypes: [
      { key: 'datasource_sync_started', label: 'Sync Started', description: 'When a data source sync begins' },
      { key: 'datasource_sync_completed', label: 'Sync Completed', description: 'When a data source sync finishes' },
      { key: 'datasource_sync_failed', label: 'Sync Failed', description: 'When a data source sync encounters an error' },
      { key: 'datasource_shared', label: 'Data Source Shared', description: 'When a data source is shared with you' },
    ],
  },
  {
    key: 'templates',
    title: 'Templates',
    description: 'Notifications for document and process templates',
    icon: PiFileTextLight,
    isComingSoon: true,
    notificationTypes: [
      { key: 'template_created', label: 'Template Created', description: 'When a template you created is available' },
      { key: 'template_updated', label: 'Template Updated', description: 'When a template you use is modified' },
      { key: 'template_shared', label: 'Template Shared', description: 'When a template is shared with you' },
      { key: 'draft_from_template_ready', label: 'Draft Ready', description: 'When a document from a template is ready' },
    ],
  },
  {
    key: 'people',
    title: 'People',
    description: 'Team member notifications',
    icon: PiUsersLight,
    isAdminOnly: true,
    isComingSoon: true,
    notificationTypes: [
      { key: 'person_completed_onboarding', label: 'Team Member Onboarded', description: 'When someone you invited completes onboarding' },
      { key: 'person_removed_from_firm', label: 'Team Member Removed', description: 'When a team member leaves' },
      { key: 'person_role_changed', label: 'Role Changed', description: 'When a team member\'s role is changed' },
      { key: 'person_profile_updated', label: 'Profile Updated', description: 'When a team member\'s profile is updated' },
    ],
  },
  {
    key: 'departments',
    title: 'Departments',
    description: 'Department activities, updates, and team feed notifications',
    icon: PiBuildingLight,
    notificationTypes: [
      { key: 'department_created', label: 'Department Created', description: 'When a new department is created' },
      { key: 'department_updated', label: 'Department Updated', description: 'When a department is modified' },
      { key: 'added_to_department', label: 'Added to Department', description: 'When you\'re added to a department' },
      { key: 'removed_from_department', label: 'Removed from Department', description: 'When you\'re removed from a department' },
      { key: 'department_announcement', label: 'Department Announcement', description: 'When an announcement is posted' },
      { key: 'department_comment', label: 'Department Comment', description: 'When someone comments in a feed' },
      { key: 'department_document_uploaded', label: 'Documents Uploaded', description: 'When new documents are added' },
    ],
  },
  {
    key: 'teams',
    title: 'Teams',
    description: 'Team collaboration, announcements, and feed notifications',
    icon: PiUsersThreeLight,
    notificationTypes: [
      { key: 'team_invited', label: 'Team Invitation', description: 'When you\'re invited to join a team' },
      { key: 'team_member_joined', label: 'Team Member Joined', description: 'When someone joins a team you\'re in' },
      { key: 'team_announcement', label: 'Team Announcement', description: 'When an announcement is posted' },
      { key: 'team_feed_post', label: 'Team Feed Post', description: 'When a team member posts to the feed' },
      { key: 'team_feed_comment', label: 'Team Feed Comment', description: 'When someone comments on a post' },
    ],
  },
  {
    key: 'help',
    title: 'Help & Support',
    description: 'Support tickets and help resources',
    icon: PiQuestionLight,
    notificationTypes: [
      { key: 'support_ticket_updated', label: 'Support Ticket Updated', description: 'When your support ticket receives a response' },
      { key: 'help_article_relevant', label: 'Relevant Help Article', description: 'When a help article matches your activity' },
      { key: 'feedback_received', label: 'Feedback Confirmation', description: 'When your feedback is received' },
    ],
  },
  {
    key: 'general',
    title: 'General',
    description: 'General account security and access notifications',
    icon: PiGearLight,
    isComingSoon: true,
    notificationTypes: [
      { key: 'permission_changed', label: 'Permission Changed', description: 'When your permissions are modified' },
      { key: 'permission_granted', label: 'New Access Granted', description: 'When you\'re granted access to a resource' },
      { key: 'security_alert', label: 'Security Alert', description: 'When unusual activity or new devices are detected' },
      { key: 'email_verified', label: 'Email Verified', description: 'When your email is verified' },
      { key: 'organization_updated', label: 'Organization Updated', description: 'When your organization\'s information changes' },
    ],
  },
  {
    key: 'billing',
    title: 'Billing',
    description: 'Billing notifications and subscription management',
    icon: PiBellLight,
    isComingSoon: true,
    notificationTypes: [],
  },
];

interface PreferencesState {
  [section: string]: Record<string, boolean | { enabled: boolean; value: number; unit: string }>;
}


interface ExpandedState {
  [key: string]: boolean;
}

const NotificationsTab: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [preferences, setPreferences] = useState<PreferencesState>({});
  const [expandedSections, setExpandedSections] = useState<ExpandedState>({});
  const [loading, setLoading] = useState(true);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'notification-settings',
    steps: notificationSettingsTourSteps,
    autoStartDelay: 600,
    readyToShow: !loading,
  });

  // Check if user is admin based on their role
  const userIsAdmin = useMemo(() => {
    if (!userInfo) return false;
    // Check for org admin flag first (most reliable)
    if (userInfo.is_org_admin) return true;

    // Fall back to checking role name (snake_case: "full_access", "managing_partner", "admin", etc.)
    const role = userInfo.role || '';
    const roleStr = typeof role === 'string' ? role : role?.name || '';
    const adminRoles = ['full_access', 'managing_partner', 'admin'];
    return adminRoles.includes(roleStr.toLowerCase());
  }, [userInfo]);

  useEffect(() => {
    const fetchPreferences = async () => {
      const prefs = await dispatch(getNotificationPreferences());
      if (prefs) {
        setPreferences(prefs);
      }
      setLoading(false);
    };
    fetchPreferences();
  }, [dispatch]);

  const visibleSections = useMemo(() => {
    return NOTIFICATION_SECTIONS.filter(section => !section.isAdminOnly || userIsAdmin);
  }, [userIsAdmin]);

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const handleToggle = async (sectionKey: string, notificationKey: string, newValue: boolean) => {
    setPreferences((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [notificationKey]: newValue,
      },
    }));

    const updatedPreferences = {
      [sectionKey]: {
        [notificationKey]: newValue,
      },
    };

    const response = await dispatch(updateNotificationPreferences(updatedPreferences));
    if (!response) {
      setPreferences((prev) => ({
        ...prev,
        [sectionKey]: {
          ...prev[sectionKey],
          [notificationKey]: !newValue,
        },
      }));
      console.error(`Failed to update preference for ${sectionKey}.${notificationKey}`);
    }
  };

  const handleCustomValueChange = async (sectionKey: string, notificationKey: string, field: 'value' | 'unit', newValue: string | number) => {
    const current = preferences[sectionKey]?.[notificationKey];
    const currentObj = typeof current === 'object' ? current : { enabled: true, value: 15, unit: 'minutes' };

    const updated = {
      ...currentObj,
      [field]: field === 'value' ? Number(newValue) : newValue,
    };

    setPreferences((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [notificationKey]: updated,
      },
    }));

    const response = await dispatch(updateNotificationPreferences({
      [sectionKey]: {
        [notificationKey]: updated,
      },
    }));

    if (!response) {
      setPreferences((prev) => ({
        ...prev,
        [sectionKey]: {
          ...prev[sectionKey],
          [notificationKey]: current,
        },
      }));
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 12 }).map((_, index) => (
          <div key={index} className="h-16 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse"></div>
        ))}
      </div>
    );
  }

  return (
    <>
    <div className="space-y-6">
      <div className="space-y-2 px-4 pt-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          {t('Notification Settings')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('Manage notifications for each feature')}
        </p>
      </div>

      <hr className="border-gray-200 dark:border-gray-700" />

      <div data-tour-id="notification-sections" className="grid grid-cols-2 2xl:grid-cols-3 gap-4 px-4 items-start">
        {visibleSections.map((section) => (
          <NotificationSection
            key={section.key}
            section={section}
            isExpanded={expandedSections[section.key] || false}
            onToggleExpand={() => toggleSection(section.key)}
            preferences={preferences[section.key] || {}}
            onToggle={(notificationKey, newValue) => handleToggle(section.key, notificationKey, newValue)}
            onCustomValueChange={(notificationKey, field, newValue) => handleCustomValueChange(section.key, notificationKey, field, newValue)}
          />
        ))}
      </div>
    </div>
    <FeatureTour
      steps={notificationSettingsTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      disableOverlay
    />
    </>
  );
};

interface NotificationSectionProps {
  section: NotificationSectionConfig;
  isExpanded: boolean;
  onToggleExpand: () => void;
  preferences: Record<string, boolean | { enabled: boolean; value: number; unit: string }>;
  onToggle: (notificationKey: string, newValue: boolean) => void;
  onCustomValueChange: (notificationKey: string, field: 'value' | 'unit', newValue: string | number) => void;
}

const NotificationSection: React.FC<NotificationSectionProps> = ({
  section,
  isExpanded,
  onToggleExpand,
  preferences,
  onToggle,
  onCustomValueChange,
}) => {
  const { t } = useTranslation();
  const Icon = section.icon;

  return (
    <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
      <button
        onClick={onToggleExpand}
        disabled={section.isComingSoon}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <div className="flex items-center gap-3 flex-1 text-left min-w-0">
          <Icon className="h-5 w-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <h4 className="font-normal text-gray-900 dark:text-gray-100 text-sm truncate">{t(section.title)}</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{t(section.description)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
          {section.isAdminOnly && (
            <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
              {t('Admin')}
            </span>
          )}
          {section.isComingSoon && (
            <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded">
              {t('Soon')}
            </span>
          )}
          {!section.isComingSoon && (
            <PiCaretDownLight
              className={`h-4 w-4 text-gray-600 dark:text-gray-400 transition-transform flex-shrink-0 ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          )}
        </div>
      </button>

      {isExpanded && !section.isComingSoon && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-4 space-y-3 bg-gray-50 dark:bg-gray-950">
          {section.notificationTypes.map((notificationType) => {
            const value = preferences[notificationType.key];
            const isEnabled = typeof value === 'boolean' ? value : value?.enabled ?? true;
            const customValue = typeof value === 'object' ? value.value : undefined;
            const customUnit = typeof value === 'object' ? value.unit : notificationType.customValueUnit;

            return (
              <div key={notificationType.key} className="bg-white dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800">
                <div className="flex items-start justify-between">
                  <div className="flex-1 pr-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t(notificationType.label)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t(notificationType.description)}</p>
                  </div>

                  {notificationType.hasCustomValue ? (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <input
                        type="number"
                        value={customValue ?? 15}
                        onChange={(e) => onCustomValueChange(notificationType.key, 'value', e.target.value)}
                        disabled={!isEnabled}
                        min={notificationType.customValueMin}
                        max={notificationType.customValueMax}
                        className="w-14 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50"
                      />
                      <select
                        value={customUnit ?? notificationType.customValueUnit}
                        onChange={(e) => onCustomValueChange(notificationType.key, 'unit', e.target.value)}
                        disabled={!isEnabled}
                        className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50"
                      >
                        {notificationType.customValueUnit === 'minutes' && (
                          <>
                            <option value="minutes">{t('min')}</option>
                          </>
                        )}
                        {notificationType.customValueUnit === 'hours' && (
                          <>
                            <option value="hours">{t('hrs')}</option>
                          </>
                        )}
                      </select>
                      <Toggle
                        enabled={isEnabled}
                        onToggle={(newValue) => onToggle(notificationType.key, newValue)}
                      />
                    </div>
                  ) : (
                    <Toggle
                      enabled={isEnabled}
                      onToggle={(newValue) => onToggle(notificationType.key, newValue)}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;
